import { useEffect, useRef } from 'react';
import { catalogOf } from '../catalog.js';
import CoverReveal from './CoverReveal.jsx';
import { Hearts, plateLabel, shout } from './print.jsx';

// The whole book, on the whole screen. Every book this theme opens opens here,
// whether a person picked it off a shelf or the level went and got one itself.
//
// There used to be a small box in the room for the second case, and the whole
// argument for it was that it did not cover the bookcases or the reader on his
// ladder. Which meant it had room for a title trimmed the way a spine is, one
// shared line of author and rating and year, and no summary at all - so the
// one screen a television ever showed by itself was the one that said least.
// This says everything instead, and the room is what it opens off of rather
// than what it must not hide.
//
// It never opens on a tap. Whoever asked for the book - a person on a spine or
// the level on its own clock - the reader walks to the shelf, goes up a ladder
// if it is one of the top three, and the sheet opens when the book is in his
// hands. See pick() in Level.jsx for why that is worth the wait.
//
// Turning to the next book is the other half of a page: a person browsing a
// shelf goes along it, so the right half of the sheet and a swipe to the right
// go forward, the left half and a swipe to the left go back. A page turn is
// the one thing that does change the sheet at once, because somebody turning
// pages is reading rather than watching the room - he catches up behind it.

// Finger travel that counts as a page turn rather than as a scroll of the
// summary. Comfortably past the fifteen pixels or so of slop a browser allows
// a tap, so a swipe never also arrives as a click.
const SWIPE_PX = 48;

// Two type sizes, both whole multiples of eight so the bitmap font's own
// pixels land on whole screen pixels. The headings take the size the level's
// status bar is using, which is what makes the sheet read as the same screen;
// the summary is a step down it, because a summary is several hundred
// characters where a title is a dozen and the point of the sheet is that all
// of it fits.
const STEP = 8;
const MIN_STEPS = 2; // 16px: below this the font is a texture rather than text
const MAX_STEPS = 5;

// And a phone, which the two sizes above cannot reach.
//
// The scale is the world's, not the screen's, and it bottoms out at 2 - so a
// 390-wide phone and a 720-wide portrait display both came out at 16px for
// everything, head and body alike. At 16px this face puts about 22 characters
// on a line, which made a Goodreads title four lines tall, no two things on
// the page a different size, and the summary twice the height of the screen
// before it had started.
//
// 12px is not a multiple of eight and is the one place in this theme that is
// not on the grid, so it was measured rather than assumed: rendered at each
// size and counted for pixels that came out an intermediate grey rather than
// ink or ground. 8, 16, 24 and 40 are exact. 12 is 1.8% grey at one device
// pixel per CSS pixel - and 0% at two and 0.6% at three, which is every phone
// this size is for. A screen with one device pixel per CSS pixel is a monitor
// or a television, and both are wider than this and never reach it.
const NARROW_PX = 520;
const NARROW_TYPE = 12;

function typeFor(scale, width) {
  if (width > 0 && width < NARROW_PX) return { head: NARROW_TYPE, body: NARROW_TYPE };
  const head = STEP * Math.min(MAX_STEPS, Math.max(MIN_STEPS, scale));
  return { head, body: Math.max(MIN_STEPS * STEP, head - 2 * STEP) };
}

// How big the cover is no longer lives here. It was a whole multiple of 24x34
// picked off the type size, which gave two sizes for every display ever made;
// it is a fraction of the viewport in the stylesheet now, and CoverReveal
// measures the box it is given. See .eb-sheet-cover.

// How long the sheet takes to arrive and to go. Matched to the keyframes by
// being handed to them rather than written down twice - see the custom
// properties on the element below.
//
// Two durations, because there are two kinds of movement here and only one of
// them is a journey. A page turn is a page sliding under a thumb and wants to
// be over before it is noticed; growing out of a book on a shelf, and shrinking
// back into it, is the sheet travelling across the whole screen from somewhere
// specific, and at 220ms that read as a flicker rather than as a movement. The
// zoom is at half the speed it was, which is the whole of the difference.
const EXIT_MS = 220;
const ZOOM_MS = 440;

// How long a book the level chose for itself stays on the screen, and how the
// page gets through itself while it is there.
//
// It used to be one number for every book: eight and a half seconds, whether
// the summary was two lines or forty. A book whose page fits had time to
// spare, and a book whose page did not showed its first screenful and then
// went back on the shelf with the rest of it unread - on a display nobody is
// touching, which is the only way that text was ever going to be seen.
//
// So the page asks for the time it needs. The base is what the level already
// gave it, which is what a page with nothing below the fold still gets; every
// line of text past the fold adds to it, up to a ceiling. The ceiling is not
// tidiness: a television that stopped on one book's page would be a poster,
// and the room is what this theme is.
//
// Then it scrolls itself, at whatever speed carries the last line up to the
// fold exactly as the hold runs out - so however much text there is, it is on
// the screen for as much of the book's time as there is. The settle at each
// end is what stops the page arriving already moving and leaving mid-line.
const READ_MS_PER_LINE = 1300;
const READ_MAX_MS = 26000;
const READ_SETTLE_MS = 1500;

export default function BookSheet({
  book, shelfKey, scale, width, auto, hold, enter, from, closing,
  onClose, onClosed, onPrev, onNext, onReading,
}) {
  const sheetRef = useRef(null);
  const bodyRef = useRef(null);
  const touch = useRef(null);
  // Set by a swipe, cleared by the touch that starts the next gesture. A
  // browser suppresses the click after a drag this long, but not every browser
  // agrees on where the line is, and a page that turned twice for one flick
  // would be the most obvious fault here.
  const swiped = useRef(false);

  const type = typeFor(scale, width);
  // A heart is six pixels tall where a capital is eight, so a heart drawn at
  // the same multiple as the lettering comes out visibly shorter than the word
  // beside it. Six rather than eight puts its height back on the cap height.
  const heartZoom = Math.max(2, Math.round(type.body / 6));

  // Growing out of the book means starting as exactly that book's rectangle:
  // scaled down to its size about the top-left corner and moved to where it is
  // standing. The same four numbers run the exit backwards.
  //
  // They are custom properties rather than a keyframe per book because there
  // is one keyframe and a different rectangle every time - a stylesheet cannot
  // know where a book on shelf four of the third bookcase ended up.
  //
  // Up here rather than beside the style it feeds, because the timer that takes
  // the sheet away has to know which of the two exits is running.
  const grow = from && from.w > 0 && from.h > 0 && from.frameW > 0 && from.frameH > 0;

  // Captured on the window rather than bubbled to it, because the level below
  // has its own arrow keys - they drive the camera - and Escape there puts a
  // book back on the shelf. While the sheet is open it is what the keyboard is
  // talking to, so it takes the key and stops it before the level sees it.
  useEffect(() => {
    function onKeyDown(event) {
      if (event.key === 'Escape') onClose();
      else if (event.key === 'ArrowRight') onNext();
      else if (event.key === 'ArrowLeft') onPrev();
      else return;
      event.preventDefault();
      event.stopPropagation();
    }
    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, [onClose, onNext, onPrev]);

  // Gone when the animation that takes it away has had its time.
  //
  // A timer rather than an animationend listener, and deliberately: this is a
  // television that runs for months, and an animation that is skipped, coalesced
  // or never started - a hidden tab, a browser that has decided to save
  // battery - fires no such event. That would leave a book's page on the screen
  // with nothing able to take it off. A timer always comes back.
  useEffect(() => {
    if (!closing) return undefined;
    // Whichever of the two the exit is actually running, or the sheet is taken
    // away while it is still shrinking.
    const timer = setTimeout(onClosed, grow ? ZOOM_MS : EXIT_MS);
    return () => clearTimeout(timer);
  }, [closing, grow, onClosed]);

  // Reading itself, when nobody asked for this book.
  //
  // One effect rather than two, because the hold and the scroll are the same
  // decision: how far there is to go is what says how long to stay, and how
  // long it stays is what says how fast to go. Splitting them would mean
  // measuring the page twice and agreeing about it.
  //
  // How far there is to go is re-read every frame. The face comes off a font
  // host and a display that has just been switched on lays this page out in
  // the fallback first, so a height taken once at mount can be the wrong one
  // by the time anybody sees it.
  useEffect(() => {
    const body = bodyRef.current;
    if (!auto || !body || closing) return undefined;

    // A line of the summary, which is what is below the fold - the scroller
    // itself has the head's line-height on it and would count wrong.
    const line = type.body * 2;
    const over = () => Math.max(0, body.scrollHeight - body.clientHeight);

    const stay = Math.min(READ_MAX_MS, hold + (over() / line) * READ_MS_PER_LINE);
    onReading(stay);

    // Nothing below the fold is a page that is simply read where it stands.
    if (over() <= 0) return undefined;

    // A person who reaches for it is reading it themselves from here on, and
    // a page being scrolled from two places at once is the worst of both.
    let stopped = false;
    const stop = () => { stopped = true; };
    body.addEventListener('wheel', stop, { passive: true });
    body.addEventListener('touchstart', stop, { passive: true });

    const startAt = performance.now() + READ_SETTLE_MS;
    const travelMs = Math.max(1, stay - 2 * READ_SETTLE_MS);
    let raf = requestAnimationFrame(function step(now) {
      raf = requestAnimationFrame(step);
      if (stopped) return;
      const at = Math.min(1, Math.max(0, (now - startAt) / travelMs));
      // Linear, like everything else here: a console scrolled a background at
      // one speed, and an eased scroll of prose reads as the page deciding
      // when you have finished a paragraph.
      body.scrollTop = at * over();
    });

    return () => {
      cancelAnimationFrame(raf);
      body.removeEventListener('wheel', stop);
      body.removeEventListener('touchstart', stop);
    };
  }, [auto, closing, hold, onReading, type.body]);

  // The carousel this panel sits in turns a vertical wheel into a horizontal
  // scroll of itself and calls preventDefault on it. Over a shelf that is
  // right; over a page of prose it means the summary does not scroll at all,
  // which is what it did - the eight-bit theme has one panel, so the carousel
  // took the wheel, had nowhere to go with it, and swallowed it.
  //
  // A native listener rather than React's onWheel, and this is the part that
  // matters: React attaches its handlers at the root of the app, which is
  // above the carousel, so an onWheel here would not run until the carousel
  // had already had the event and cancelled it. Only stopPropagation - the
  // default action is the scroll we want.
  useEffect(() => {
    const el = sheetRef.current;
    if (!el) return undefined;
    const keep = (event) => event.stopPropagation();
    el.addEventListener('wheel', keep);
    return () => el.removeEventListener('wheel', keep);
  }, []);

  function onTouchStart(event) {
    swiped.current = false;
    const finger = event.touches[0];
    touch.current = finger ? { x: finger.clientX, y: finger.clientY } : null;
  }

  function onTouchEnd(event) {
    const from = touch.current;
    touch.current = null;
    const finger = event.changedTouches[0];
    if (!from || !finger) return;

    const dx = finger.clientX - from.x;
    const dy = finger.clientY - from.y;
    // A drag that is mostly vertical is the summary being scrolled, not a page
    // being turned. Compared against each other rather than against a second
    // threshold, so a long diagonal still counts as whichever it mostly was.
    if (Math.abs(dx) < SWIPE_PX || Math.abs(dx) <= Math.abs(dy)) return;
    swiped.current = true;
    if (dx > 0) onNext();
    else onPrev();
  }

  function onClick(event) {
    if (swiped.current) return;
    const bounds = event.currentTarget.getBoundingClientRect();
    if (event.clientX - bounds.left < bounds.width / 2) onPrev();
    else onNext();
  }

  const average = book.rating || 0;
  const { genres, subjects } = catalogOf(book);

  const style = {
    fontSize: `${type.head}px`,
    lineHeight: `${type.head}px`,
    '--eb-exit': `${EXIT_MS}ms`,
    '--eb-zoom': `${ZOOM_MS}ms`,
    ...(grow ? {
      '--eb-from-x': `${Math.round(from.x)}px`,
      '--eb-from-y': `${Math.round(from.y)}px`,
      '--eb-from-sx': from.w / from.frameW,
      '--eb-from-sy': from.h / from.frameH,
    } : {}),
  };

  // Which way it arrived, and then - whatever way that was - the one way it
  // leaves, which is back into the shelf.
  const how = closing
    ? (grow ? 'eb-sheet--shrink' : 'eb-sheet--fade')
    : { next: 'eb-sheet--next', prev: 'eb-sheet--prev' }[enter] || (grow ? 'eb-sheet--grow' : 'eb-sheet--open');

  return (
    <div
      className={`eb-sheet ${how}`}
      ref={sheetRef}
      role="dialog"
      aria-modal="true"
      aria-label={book.title}
      style={style}
      onClick={onClick}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      <div className="eb-sheet-bar">
        <span className="eb-sheet-shelf">{plateLabel(shelfKey)}</span>
        {/* The same two buttons the level's status bar carries, doing the same
            kind of thing one level down: there they step along the shelves,
            here they step along the books. One object, one look, one place to
            learn what an arrow on a brass plate does - and in the same place
            on the bar, which is the middle of it, so that opening a book does
            not move the thing your hand is already going to.

            In the bar rather than in a row of their own at the foot of the
            page, which is where they were. That row was two labelled buttons
            somebody had to reach the bottom of a summary to see. */}
        <span className="eb-sheet-turn">
          <button
            type="button"
            className="eb-sheet-plate"
            aria-label="Previous book"
            onClick={(event) => { event.stopPropagation(); onPrev(); }}
          >
            <span>{'<'}</span>
          </button>
          <button
            type="button"
            className="eb-sheet-plate"
            aria-label="Next book"
            onClick={(event) => { event.stopPropagation(); onNext(); }}
          >
            <span>{'>'}</span>
          </button>
        </span>
        <button
          type="button"
          className="eb-sheet-plate eb-sheet-close"
          aria-label="Close"
          // The bar is inside the half that would turn the page, so the button
          // has to keep the click to itself.
          onClick={(event) => { event.stopPropagation(); onClose(); }}
        >
          <span>X</span>
        </button>
      </div>

      {/* The one thing on the sheet that scrolls, and the reason the swipe
          above insists on being horizontal. A summary is longer than any
          screen at this type size, and the alternative - clamping it to what
          fits - is the thing the sheet exists to stop doing. */}
      <div className="eb-sheet-body" ref={bodyRef}>
        <div className="eb-sheet-head">
          {/* The drawn portrait first, then the jacket itself, resolving out
              of it in four steps - see CoverReveal.jsx. The frame is sized
              here because it is this layout's business how big the cover is,
              and every step fills it, so nothing moves while it sharpens. */}
          <div className="eb-sheet-cover">
            <CoverReveal book={book} />
          </div>
          {/* Everything the jacket would tell you, beside the jacket: what it
              is called, who wrote it, and the four facts. The summary is the
              one thing that goes below, because it is prose and prose wants
              the whole width - a paragraph in a column beside a cover is a
              newspaper, and this is a book. */}
          <div className="eb-sheet-detail">
            <div className="eb-sheet-titles">
              {/* In full, and not shortTitle: a spine is trimmed because a spine
                  is eight pixels wide, and the series a Goodreads title repeats
                  is part of what somebody who picked this book up wanted to
                  know. */}
              <h2 className="eb-sheet-title">{shout(book.title)}</h2>
              <p className="eb-sheet-by">{shout(book.author)}</p>
            </div>

            <dl className="eb-sheet-facts" style={{ fontSize: `${type.body}px`, lineHeight: `${type.body * 2}px` }}>
              {/* Both ratings, named. They are different numbers about different
                  people - what the reader thought of it, and what everybody else
                  did - and a single row of hearts can only be one of them without
                  saying which. */}
              <div className="eb-sheet-fact">
                <dt>YOUR RATING</dt>
                <dd>
                  {book.userRating > 0
                    ? <Hearts rating={book.userRating} zoom={heartZoom} />
                    : <span className="eb-sheet-none">NOT RATED</span>}
                </dd>
              </div>
              <div className="eb-sheet-fact">
                <dt>AVERAGE</dt>
                <dd>
                  {average > 0 ? (
                    <>
                      <Hearts rating={average} zoom={heartZoom} />
                      <span className="eb-sheet-number">{average.toFixed(2)}</span>
                    </>
                  ) : <span className="eb-sheet-none">NOT RATED</span>}
                </dd>
              </div>
              {book.publishedYear ? (
                <div className="eb-sheet-fact">
                  <dt>PUBLISHED</dt>
                  <dd><span className="eb-sheet-number">{book.publishedYear}</span></dd>
                </div>
              ) : null}
              {book.pages ? (
                <div className="eb-sheet-fact">
                  <dt>PAGES</dt>
                  <dd><span className="eb-sheet-number">{book.pages}</span></dd>
                </div>
              ) : null}
              {/* What the Library of Congress says it is. Whole rows rather than
                  cells: these are lists, a cell is sized for one number, and
                  a genre wrapped inside a 24em track reads as a broken row.
                  Absent rather than "UNKNOWN" when the catalogue has nothing,
                  because that is a fact about the catalogue, not the book. */}
              {genres.length > 0 ? (
                <div className="eb-sheet-fact eb-sheet-fact--wide">
                  <dt>{genres.length > 1 ? 'GENRES' : 'GENRE'}</dt>
                  <dd><span className="eb-sheet-words">{shout(genres.join(', '))}</span></dd>
                </div>
              ) : null}
              {subjects.length > 0 ? (
                <div className="eb-sheet-fact eb-sheet-fact--wide">
                  <dt>ABOUT</dt>
                  <dd><span className="eb-sheet-words">{shout(subjects.join(', '))}</span></dd>
                </div>
              ) : null}
            </dl>
          </div>
        </div>

        <p className="eb-sheet-summary" style={{ fontSize: `${type.body}px`, lineHeight: `${type.body * 2}px` }}>
          {shout(book.description) || 'NO SUMMARY FOR THIS ONE YET.'}
        </p>
      </div>

    </div>
  );
}
